/**
 * Admin Ambient Photos Page
 * Manage restaurant ambient photos that appear in generated videos
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SortableAmbientPhotosGrid } from '@/components/admin/cards/SortableAmbientPhotosGrid';
import { PhotoUploadZone } from '@/components/admin/forms/PhotoUploadZone';
import { ambientPhotoService } from '@/services/ambientPhotoService';
import { AdminPageShell, PageHeader } from '@/components/admin/shell';
import { AdminButton } from '@/components/admin/shell/AdminButton';

const MAX_PHOTOS = 10;

export function AmbientPhotosPage() {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);

  // Fetch ambient photos
  const {
    data: photos = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['ambient-photos'],
    queryFn: () => ambientPhotoService.listPhotos(),
  });

  // Delete photo mutation
  const deleteMutation = useMutation({
    mutationFn: (photoId: string) => ambientPhotoService.deletePhoto(photoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ambient-photos'] });
      toast.success('Foto eliminada correctamente');
    },
    onError: (error: unknown) => {
      const apiError = error as { response?: { data?: { error?: string } } };
      const message = apiError?.response?.data?.error || 'Error al eliminar la foto';
      toast.error(message);
    },
  });

  /**
   * Handle photo uploads
   */
  const handleUpload = async (files: File[]) => {
    setUploading(true);

    let successCount = 0;
    let failCount = 0;

    for (const file of files) {
      try {
        await ambientPhotoService.uploadPhoto(file, (progress) => {
          // Progress tracking could be shown in UI if needed
          console.log(`Uploading ${file.name}: ${progress}%`);
        });
        successCount++;
      } catch (error: unknown) {
        failCount++;
        const apiError = error as { response?: { data?: { error?: string } } };
        const message = apiError?.response?.data?.error || `Error al subir ${file.name}`;
        toast.error(message);
      }
    }

    setUploading(false);

    // Refetch photos after uploads
    if (successCount > 0) {
      await queryClient.invalidateQueries({ queryKey: ['ambient-photos'] });

      if (failCount === 0) {
        toast.success(
          `${successCount} foto${successCount > 1 ? 's' : ''} subida${successCount > 1 ? 's' : ''} correctamente`
        );
      } else {
        toast.warning(
          `${successCount} foto${successCount > 1 ? 's' : ''} subida${successCount > 1 ? 's' : ''}, ${failCount} fallida${failCount > 1 ? 's' : ''}`
        );
      }
    }
  };

  /**
   * Handle photo deletion
   */
  const handleDelete = async (photoId: string) => {
    await deleteMutation.mutateAsync(photoId);
  };

  /**
   * Handle photo reordering
   */
  const handleReorder = async (photoIds: string[]) => {
    await ambientPhotoService.reorderPhotos(photoIds);
    await queryClient.invalidateQueries({ queryKey: ['ambient-photos'] });
  };

  return (
    <AdminPageShell>
      <div className="space-y-6">
        <PageHeader
          title="Fotos de Ambiente"
          subtitle="Gestiona las fotos de tu restaurante que se incluirán al final de todos los videos generados"
        />

        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">Cómo funcionan las fotos de ambiente</p>
                <ul className="list-disc list-inside space-y-1 text-blue-800">
                  <li>Estas fotos se muestran al final de cada video generado</li>
                  <li>Arrastra las fotos para reordenarlas según prefieras</li>
                  <li>El orden se guarda automáticamente</li>
                  <li>Puedes subir hasta {MAX_PHOTOS} fotos</li>
                  <li>Formatos: PNG, JPG o JPEG (máx. 20MB por foto)</li>
                  <li>Los cambios solo afectan a los videos generados después del cambio</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Subir Fotos</CardTitle>
          </CardHeader>
          <CardContent>
            <PhotoUploadZone
              onUpload={handleUpload}
              maxPhotos={MAX_PHOTOS}
              currentCount={photos.length}
              uploading={uploading}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                Fotos Actuales ({photos.length}/{MAX_PHOTOS})
              </CardTitle>
              <AdminButton
                size="sm"
                onClick={() => refetch()}
                isLoading={isLoading}
              >
                Actualizar
              </AdminButton>
            </div>
          </CardHeader>
          <CardContent>
            <SortableAmbientPhotosGrid
              photos={photos}
              onDelete={handleDelete}
              loading={isLoading}
              onReorder={handleReorder}
            />
          </CardContent>
        </Card>
      </div>
    </AdminPageShell>
  );
}
